/**
 * I passi per impostare, cambiare e disattivare il PIN (SPEC §3.15).
 *
 * ## Perché una macchina a stati e non tre schermate
 *
 * Perché sono la stessa schermata con una domanda diversa: un tastierino, quattro pallini, una
 * frase. Scriverle come tre componenti vorrebbe dire tre volte la stessa gestione del tastierino, e
 * la quarta volta che si tocca una delle tre le altre due divergono.
 *
 * E perché **qui non c'è niente da disegnare**: sono regole su quattro cifre, e si provano in Node.
 * Quel che resta fuori è un pallino pieno e un pallino vuoto.
 *
 * ## Nessuna scala d'età, e non è una dimenticanza
 *
 * Il prototipo ha una soglia configurabile — 14+ / 16+ / 18+ — e la decisione D-6 l'ha cancellata:
 * una soglia funzionerebbe sulla parte di catalogo che ha una classificazione e non sull'altra, e
 * una soglia che copre metà catalogo è **peggio** di nessuna soglia, perché un genitore la imposta e
 * si fida. Vedi la nota in testa a `parentalControl.ts`. Qui il PIN è un interruttore: impostato o
 * no, e protegge quel che il backend marca `is_adult`.
 *
 * ## Il PIN dimenticato non si recupera, e va detto prima
 *
 * Non sta sul server — non ci è mai stato — quindi non c'è nessuno a cui chiederlo. L'unica strada è
 * cancellare il profilo da questo apparecchio. Dirlo mentre lo si imposta sarebbe rumore; dirlo
 * quando qualcuno lo sta cercando è l'unica informazione utile in quel momento.
 */

import { PIN_LENGTH } from './parentalControl.js';

/** Cosa si sta facendo. `null` = il menu, quando un PIN c'è già. */
export type PinIntent = 'set' | 'change' | 'disable';

/** Quale domanda è a schermo adesso. */
export type PinStep =
  /** «Scegli un PIN di 4 cifre». */
  | 'choose'
  /** «Conferma il nuovo PIN». */
  | 'confirm'
  /** «Inserisci il PIN attuale». */
  | 'current';

/** Perché il passo precedente non è andato. */
export type PinError = 'wrong' | 'mismatch';

export interface PinFlow {
  readonly intent: PinIntent;
  readonly step: PinStep;
  /** Le cifre digitate finora, da zero a quattro. */
  readonly entered: string;
  /** Il PIN scelto al passo precedente, che il passo «conferma» deve ritrovare uguale. */
  readonly chosen: string;
  readonly error: PinError | null;
}

/** Quanti passi ha questo percorso, per scrivere «Passo 2 di 3». */
export function pinStepCount(intent: PinIntent): number {
  if (intent === 'change') return 3;
  if (intent === 'set') return 2;
  return 1;
}

/** A che punto si è, da uno. */
export function pinStepNumber(flow: PinFlow): number {
  if (flow.intent === 'change') return flow.step === 'current' ? 1 : flow.step === 'choose' ? 2 : 3;
  if (flow.intent === 'set') return flow.step === 'choose' ? 1 : 2;
  return 1;
}

/** L'inizio di un percorso. */
export function startPinFlow(intent: PinIntent): PinFlow {
  return {
    intent,
    // Cambiare e disattivare cominciano dal PIN attuale: è l'unica prova che chi sta al telecomando
    // è chi lo ha impostato.
    step: intent === 'set' ? 'choose' : 'current',
    entered: '',
    chosen: '',
    error: null,
  };
}

/** Cosa fare dopo aver digitato una cifra. */
export type PinOutcome =
  /** Si continua: la schermata mostra `flow`. */
  | { readonly kind: 'continue'; readonly flow: PinFlow }
  /** Il percorso è finito: `pin` è il nuovo PIN, oppure `null` per «disattivato». */
  | { readonly kind: 'done'; readonly pin: string | null };

/**
 * Una cifra in più.
 *
 * Alla quarta si verifica **da sé**: chiedere anche un OK dopo l'ultima cifra è una pressione che
 * non aggiunge niente, e su un telecomando ogni pressione si sente.
 */
export function pressPinDigit(flow: PinFlow, digit: string, storedPin: string | null): PinOutcome {
  if (!/^[0-9]$/.test(digit)) return { kind: 'continue', flow };
  if (flow.entered.length >= PIN_LENGTH) return { kind: 'continue', flow };

  // Digitando, l'errore di prima sparisce: resta finché non si riprova, non oltre.
  const entered = flow.entered + digit;
  if (entered.length < PIN_LENGTH) {
    return { kind: 'continue', flow: { ...flow, entered, error: null } };
  }

  if (flow.step === 'current') {
    if (storedPin === null || entered !== storedPin) {
      // Sbagliato: si svuotano i pallini e si resta **qui**. Riportare al menu vorrebbe dire
      // rifare tutto il percorso per un errore di battitura.
      return { kind: 'continue', flow: { ...flow, entered: '', error: 'wrong' } };
    }
    if (flow.intent === 'disable') return { kind: 'done', pin: null };
    return { kind: 'continue', flow: { ...flow, step: 'choose', entered: '', error: null } };
  }

  if (flow.step === 'choose') {
    return { kind: 'continue', flow: { ...flow, step: 'confirm', entered: '', chosen: entered, error: null } };
  }

  if (entered !== flow.chosen) {
    /*
      Non coincidono: si riparte dalla **scelta**, non dalla conferma.

      Chi ha sbagliato non sa quale dei due è quello che voleva — può aver sbagliato il primo — e
      rimandarlo a confermare un PIN di cui non si fida più è una trappola.
    */
    return { kind: 'continue', flow: { ...flow, step: 'choose', entered: '', chosen: '', error: 'mismatch' } };
  }

  return { kind: 'done', pin: entered };
}

/** Cancella l'ultima cifra. */
export function erasePinDigit(flow: PinFlow): PinFlow {
  if (flow.entered === '') return flow;
  return { ...flow, entered: flow.entered.slice(0, -1), error: null };
}
